import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { AttachmentsPanel } from '../../components/data/AttachmentsPanel.jsx';
import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency } from '../../utils/formatters.js';

/** Assignment card: pick one of the ACTIVE price lists (or none = list price). */
function PriceListCard({ customer }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(customer.priceListId || '');

  const priceLists = useQuery({
    queryKey: ['sales', 'price-lists', 'list'],
    queryFn: () => salesApi.listPriceLists().then((r) => r.items || []),
  });
  const activeLists = (priceLists.data || []).filter(
    // Keep the currently assigned list selectable even if it has been deactivated.
    (pl) => pl.isActive || pl.id === customer.priceListId,
  );

  const save = useMutation({
    mutationFn: () => salesApi.updateCustomer(customer.id, { priceListId: value || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'customer', customer.id] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'price-lists'] });
      showToast(t('customer.priceListSaved'), 'success');
    },
    onError: (err) => showToast(err?.message || t('common.loadError'), 'danger'),
  });

  return (
    <Card title={t('priceList.assign')}>
      <div className="field-row" style={{ alignItems: 'flex-end' }}>
        <label className="field">
          <span className="field-label">{t('priceList.assign')}</span>
          <select className="select" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">{t('priceList.none')}</option>
            {activeLists.map((pl) => (
              <option key={pl.id} value={pl.id}>{pl.name}</option>
            ))}
          </select>
        </label>
        <Button
          variant="primary"
          style={{ flex: '0 0 auto' }}
          disabled={save.isPending || value === (customer.priceListId || '')}
          onClick={() => save.mutate()}
        >
          {save.isPending ? `${t('common.save')}…` : t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();

  const customer = useQuery({
    queryKey: ['sales', 'customer', id],
    queryFn: () => salesApi.getCustomer(id).then((r) => r.items),
  });

  if (customer.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (!customer.data) {
    return (
      <section className="page">
        <DetailHeader backTo="/sales/customers" title={t('sales.customers.title')} />
        <EmptyState message={t('common.noData')} />
      </section>
    );
  }

  const c = customer.data;

  return (
    <section className="page">
      <DetailHeader
        backTo="/sales/customers"
        title={c.name}
        status={c.status}
      />

      <Card title={t('detail.header')}>
        <FieldGrid
          items={[
            { label: t('col.code'), value: c.code },
            { label: t('col.email'), value: c.email },
            { label: t('settings.company.phone'), value: c.phone },
            { label: t('sales.customers.col.creditLimit'), value: formatCurrency(c.creditLimit, c.currency) },
            { label: t('settings.currency'), value: c.currency },
            { label: t('col.status'), value: <Badge status={c.status} /> },
          ]}
        />
      </Card>

      <PriceListCard key={`${c.id}-${c.priceListId || 'none'}`} customer={c} />

      <AttachmentsPanel
        queryKey={['sales', 'customer-attachments', id]}
        api={{
          list: () => salesApi.listCustomerAttachments(id).then((r) => r.items.attachments),
          upload: (body) => salesApi.uploadCustomerAttachment(id, body),
          remove: (attId) => salesApi.deleteAttachment(attId),
          download: (attId, name) => salesApi.downloadAttachment(attId, name),
        }}
      />
    </section>
  );
}
